// src/prototype/exports/exportSepPdf.ts
// ============================================================
// SEP PDF EXPORT
// Captures the full Strategic Execution Plan report and writes it
// to a multi-page tabloid-size PDF (11×17 in, content scaled to page width).
//
// Pipeline: clone DOM into a hidden iframe (fixed 1100 px width) →
// inject capture CSS + normalize colors → await iframe fonts →
// capture via html-to-image → crop by section pairs → one PDF page per pair.
// ============================================================

import { SEP_SECTIONS } from '../components/sep/SEPReportIndex';
import { buildFilename } from './downloadHelper';

const EXPORT_WIDTH_PX = 1100;
const PDF_PAGE_FORMAT = 'tabloid' as const;
const PDF_ORIENTATION = 'portrait' as const;
/** Uniform margin on all sides (~0.5 in). */
const PDF_PAGE_MARGIN_PT = 36;
/** Extra top inset on page 1 only (~0.33 in). */
const PDF_FIRST_PAGE_TOP_MARGIN_PT = 24;
const MAX_CANVAS_PX = 16384;
/** Extra CSS pixels included when cropping so 1px borders are not clipped. */
const CROP_EDGE_PADDING_CSS = 4;

/** Which report sections (0-based index) appear on each PDF page. */
const SEP_PDF_PAGE_LAYOUT: ReadonlyArray<{ sections: readonly number[] }> = [
  { sections: [0, 1] }, // 1 & 2
  { sections: [2, 3] }, // 3 & 4
  { sections: [4] },    // 5 only
  { sections: [5] },    // 6 only
  { sections: [6] },    // 7 only
  { sections: [7, 8] }, // 8 & 9
  { sections: [9] },    // 10 only
];

interface SectionBounds {
  id: string;
  top: number;
  height: number;
}

function buildSectionGroups(
  sectionBounds: SectionBounds[]
): Array<{ bounds: SectionBounds[] }> {
  return SEP_PDF_PAGE_LAYOUT.map(({ sections }) => ({
    bounds: sections.map((index) => {
      const bound = sectionBounds[index];
      if (!bound) {
        throw new Error(`Section index ${index} is missing from the export capture.`);
      }
      return bound;
    }),
  }));
}

function measureSectionBounds(root: HTMLElement): SectionBounds[] {
  const rootRect = root.getBoundingClientRect();
  return SEP_SECTIONS.map(({ id }) => {
    const el = root.querySelector(`#${id}`);
    if (!el || !(el instanceof HTMLElement)) {
      throw new Error(`Section #${id} is missing from the export capture.`);
    }
    const rect = el.getBoundingClientRect();
    return {
      id,
      top: rect.top - rootRect.top,
      height: rect.height,
    };
  });
}

function computeGroupCropBounds(
  group: SectionBounds[],
  sectionBounds: SectionBounds[]
): { topCss: number; heightCss: number } {
  const firstIndex = sectionBounds.findIndex((bound) => bound.id === group[0].id);
  const lastIndex = sectionBounds.findIndex((bound) => bound.id === group[group.length - 1].id);
  const nextBound = sectionBounds[lastIndex + 1];

  const topCss = Math.max(0, group[0].top - (firstIndex === 0 ? 0 : CROP_EDGE_PADDING_CSS));
  let bottomCss = group[group.length - 1].top + group[group.length - 1].height + CROP_EDGE_PADDING_CSS;

  if (nextBound) {
    bottomCss = Math.min(bottomCss, nextBound.top - 1);
  }

  return { topCss, heightCss: Math.max(1, bottomCss - topCss) };
}

function cropCanvas(
  source: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number
): HTMLCanvasElement {
  const cropped = document.createElement('canvas');
  cropped.width = sw;
  cropped.height = sh;
  const ctx = cropped.getContext('2d');
  if (!ctx) {
    throw new Error('Could not crop the captured report image.');
  }
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  return cropped;
}

const CAPTURE_NORMALIZE_CSS = `
*, *::before, *::after {
  text-rendering: geometricPrecision !important;
  -webkit-font-smoothing: antialiased !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  line-height: normal !important;
  animation: none !important;
  animation-duration: 0s !important;
  animation-delay: 0s !important;
  transition: none !important;
  transition-duration: 0s !important;
  transition-delay: 0s !important;
}
#sep-report-export-root {
  display: flex !important;
  flex-direction: column !important;
  gap: 24px !important;
  padding-bottom: 8px !important;
  box-sizing: border-box !important;
}
#sep-report-export-root > div[id^="sep-"] > div {
  margin-bottom: 0 !important;
  box-sizing: border-box !important;
}
`;

const COLOR_PROPERTIES = [
  'color',
  'backgroundColor',
  'backgroundImage',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'boxShadow',
  'textDecorationColor',
  'columnRuleColor',
  'caretColor',
  'fill',
  'stroke',
] as const;

const MODERN_COLOR_RE = /(oklch|oklab|lch|lab|hwb|color-mix|color\()/i;
const COLOR_FN_RE =
  /\b(?:oklch|oklab|color-mix|hwb|lch|lab|color)\([^()]*(?:\([^()]*\)[^()]*)*\)/gi;

let conversionCtx: CanvasRenderingContext2D | null = null;
function getConversionCtx(): CanvasRenderingContext2D | null {
  if (conversionCtx) return conversionCtx;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  conversionCtx = canvas.getContext('2d', { willReadFrequently: true });
  return conversionCtx;
}

function convertColorToken(token: string): string {
  const ctx = getConversionCtx();
  if (!ctx) return token;
  try {
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    ctx.fillStyle = token.trim();
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    return a === 255
      ? `rgb(${r}, ${g}, ${b})`
      : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
  } catch {
    return token;
  }
}

function normalizeColorValue(value: string): string {
  if (!value || value.indexOf('(') === -1 || !MODERN_COLOR_RE.test(value)) {
    return value;
  }
  return value.replace(COLOR_FN_RE, (match) => convertColorToken(match));
}

function processCssRule(rule: CSSRule): void {
  const styleRule = rule as CSSRule & { style?: CSSStyleDeclaration };
  const style = styleRule.style;
  if (style) {
    for (let i = 0; i < style.length; i++) {
      const prop = style.item(i);
      const value = style.getPropertyValue(prop);
      if (value && MODERN_COLOR_RE.test(value)) {
        try {
          style.setProperty(prop, normalizeColorValue(value), style.getPropertyPriority(prop));
        } catch {
          /* ignore unsettable declarations */
        }
      }
    }
  }
  const groupRule = rule as CSSRule & { cssRules?: CSSRuleList };
  if (groupRule.cssRules) {
    for (const child of Array.from(groupRule.cssRules)) {
      processCssRule(child);
    }
  }
}

function normalizeStylesheets(doc: Document): void {
  for (const sheet of Array.from(doc.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      try {
        processCssRule(rule);
      } catch {
        /* ignore individual rule failures */
      }
    }
  }
}

function normalizeColorsForCapture(root: HTMLElement): void {
  const win = root.ownerDocument?.defaultView;
  if (!win) return;

  const elements: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  for (const el of elements) {
    const computed = win.getComputedStyle(el);
    for (const prop of COLOR_PROPERTIES) {
      const value = computed.getPropertyValue(
        prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)
      );
      if (value && MODERN_COLOR_RE.test(value)) {
        try {
          el.style.setProperty(
            prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`),
            normalizeColorValue(value)
          );
        } catch {
          /* ignore unsettable props */
        }
      }
    }
  }
}

function copyDocumentStyles(sourceDoc: Document, targetDoc: Document): void {
  const base = sourceDoc.querySelector('base');
  if (base) {
    targetDoc.head.appendChild(base.cloneNode(true));
  }

  for (const node of sourceDoc.querySelectorAll('link[rel="stylesheet"], style')) {
    targetDoc.head.appendChild(node.cloneNode(true));
  }
}

function injectCaptureCss(doc: Document): void {
  const style = doc.createElement('style');
  style.id = 'sep-pdf-capture-normalize';
  style.textContent = CAPTURE_NORMALIZE_CSS;
  doc.head.appendChild(style);
}

function createCaptureIframe(initialHeight: number): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.width = String(EXPORT_WIDTH_PX);
  iframe.height = String(initialHeight);
  iframe.style.cssText = [
    'position:fixed',
    'left:-99999px',
    'top:0',
    'border:0',
    'margin:0',
    'padding:0',
    'visibility:hidden',
    'pointer-events:none',
    `width:${EXPORT_WIDTH_PX}px`,
    `height:${initialHeight}px`,
  ].join(';');
  return iframe;
}

async function waitForLayout(win: Window): Promise<void> {
  await new Promise<void>((resolve) => {
    win.requestAnimationFrame(() => {
      win.requestAnimationFrame(() => resolve());
    });
  });
}

async function waitForIframeFonts(sourceDoc: Document, iframeDoc: Document): Promise<void> {
  const iframeFonts = iframeDoc.fonts;
  const sourceFonts = sourceDoc.fonts;

  if (sourceFonts && iframeFonts) {
    const loads: Promise<FontFace[] | undefined>[] = [];
    sourceFonts.forEach((face) => {
      if (face.status === 'loaded') {
        loads.push(
          iframeFonts.load(`${face.weight} ${face.size}px ${face.family}`).catch(() => undefined)
        );
      }
    });
    await Promise.all(loads);
  }

  if (iframeFonts?.ready) {
    try {
      await iframeFonts.ready;
    } catch {
      /* non-fatal */
    }
  }
}

function measureCaptureHeight(root: HTMLElement, iframeDoc: Document): number {
  return Math.max(
    root.scrollHeight,
    root.offsetHeight,
    iframeDoc.body.scrollHeight,
    iframeDoc.documentElement.scrollHeight,
    1
  );
}

function computePixelRatio(width: number, height: number): number {
  const desired = Math.max(2, window.devicePixelRatio || 2);
  return Math.max(0.1, Math.min(desired, MAX_CANVAS_PX / width, MAX_CANVAS_PX / height));
}

function prepareCaptureClone(source: HTMLElement, iframeDoc: Document, backgroundColor: string): HTMLElement {
  copyDocumentStyles(document, iframeDoc);
  injectCaptureCss(iframeDoc);

  iframeDoc.body.style.margin = '0';
  iframeDoc.body.style.padding = '0';
  iframeDoc.body.style.width = `${EXPORT_WIDTH_PX}px`;
  iframeDoc.body.style.backgroundColor = backgroundColor;
  iframeDoc.body.style.overflow = 'visible';

  const clone = source.cloneNode(true) as HTMLElement;
  clone.style.width = `${EXPORT_WIDTH_PX}px`;
  clone.style.maxWidth = `${EXPORT_WIDTH_PX}px`;
  clone.style.minWidth = `${EXPORT_WIDTH_PX}px`;
  clone.style.boxSizing = 'border-box';
  clone.style.overflow = 'visible';
  clone.style.height = 'auto';

  iframeDoc.body.appendChild(clone);

  try {
    normalizeStylesheets(iframeDoc);
  } catch (err) {
    console.warn('[exportSepPdf] stylesheet normalization skipped:', err);
  }
  try {
    normalizeColorsForCapture(clone);
  } catch (err) {
    console.warn('[exportSepPdf] element color normalization skipped:', err);
  }

  return clone;
}

export interface ExportSepPdfOptions {
  /** The element whose full scroll-height should be captured. */
  element?: HTMLElement | null;
  /**
   * Id of the element to capture. PREFERRED over `element`: it is re-resolved
   * from the live DOM immediately before capture so a React re-render during
   * the async setup cannot leave us holding a detached node.
   */
  elementId?: string;
  /** Client identifier used in the generated filename. */
  clientIdentifier?: string;
  /** Page background color (defaults to the app canvas color). */
  backgroundColor?: string;
}

function computeImagePlacement(
  cssWidth: number,
  heightCss: number,
  marginPt: number,
  topMarginPt: number,
  maxWidthPt: number,
  maxHeightPt: number
): { xPt: number; yPt: number; imgWidthPt: number; imgHeightPt: number } {
  const aspect = cssWidth / heightCss;

  let imgWidthPt = maxWidthPt;
  let imgHeightPt = imgWidthPt / aspect;

  if (imgHeightPt > maxHeightPt) {
    imgHeightPt = maxHeightPt;
    imgWidthPt = imgHeightPt * aspect;
  }

  return {
    xPt: marginPt,
    yPt: topMarginPt,
    imgWidthPt,
    imgHeightPt,
  };
}

function writeSectionGroupsToPdf(
  jsPDFCtor: typeof import('jspdf').jsPDF,
  canvas: HTMLCanvasElement,
  cssWidth: number,
  sectionBounds: SectionBounds[],
  filename: string
): void {
  const pixelScale = canvas.width / cssWidth;
  const sectionGroups = buildSectionGroups(sectionBounds);

  const pdf = new jsPDFCtor({
    orientation: PDF_ORIENTATION,
    unit: 'pt',
    format: PDF_PAGE_FORMAT,
    compress: true,
  });

  const pageWidthPt = pdf.internal.pageSize.getWidth();
  const pageHeightPt = pdf.internal.pageSize.getHeight();
  const marginPt = PDF_PAGE_MARGIN_PT;
  const maxWidthPt = pageWidthPt - marginPt * 2;

  sectionGroups.forEach(({ bounds: group }, pageIndex) => {
    if (pageIndex > 0) {
      pdf.addPage(PDF_PAGE_FORMAT, PDF_ORIENTATION);
    }

    const { topCss, heightCss } = computeGroupCropBounds(group, sectionBounds);

    const cropY = Math.max(0, Math.floor(topCss * pixelScale));
    const cropH = Math.max(1, Math.ceil(heightCss * pixelScale));
    const cropW = canvas.width;
    const maxCropH = canvas.height - cropY;

    const cropped = cropCanvas(canvas, 0, cropY, cropW, Math.min(cropH, maxCropH));

    let imgData: string;
    try {
      imgData = cropped.toDataURL('image/jpeg', 0.95);
    } catch (err) {
      console.error('[exportSepPdf] canvas.toDataURL failed:', err);
      throw new Error(
        `Could not read the rendered image (${describeError(err)}). ` +
          'This usually means the report contains an image from another domain without CORS.'
      );
    }

    if (!imgData || imgData === 'data:,') {
      throw new Error('The rendered image could not be encoded (empty data URL).');
    }

    const topMarginPt = pageIndex === 0 ? PDF_FIRST_PAGE_TOP_MARGIN_PT : marginPt;
    const maxHeightPt = pageHeightPt - topMarginPt - marginPt;
    const { xPt, yPt, imgWidthPt, imgHeightPt } = computeImagePlacement(
      cssWidth,
      heightCss,
      marginPt,
      topMarginPt,
      maxWidthPt,
      maxHeightPt
    );

    pdf.addImage(
      imgData,
      'JPEG',
      xPt,
      yPt,
      imgWidthPt,
      imgHeightPt,
      undefined,
      'FAST'
    );
  });

  try {
    pdf.save(filename);
  } catch (err) {
    console.error('[exportSepPdf] writing the PDF failed:', err);
    throw new Error(`Writing the PDF failed (${describeError(err)})`);
  }
}

/**
 * Renders the given element to a multi-page tabloid-size PDF using a fixed
 * section-to-page layout. Content is top-aligned at full content width.
 */
export async function exportSepPdf({
  element,
  elementId,
  clientIdentifier = 'Client',
  backgroundColor = '#F5F7FA',
}: ExportSepPdfOptions): Promise<void> {
  let toCanvas: typeof import('html-to-image').toCanvas;
  let jsPDF: typeof import('jspdf').jsPDF;
  try {
    const [htmlToImage, jspdfMod] = await Promise.all([import('html-to-image'), import('jspdf')]);
    toCanvas = htmlToImage.toCanvas;
    jsPDF = jspdfMod.jsPDF;
  } catch (err) {
    throw new Error(`Could not load export libraries: ${describeError(err)}`);
  }

  const target =
    (elementId ? document.getElementById(elementId) : null) ??
    (element && element.isConnected ? element : null);

  if (!target || !target.isConnected) {
    throw new Error('The report is no longer on the page — please try exporting again.');
  }

  const initialHeight = Math.max(target.scrollHeight, target.offsetHeight, 1);
  const iframe = createCaptureIframe(initialHeight);
  document.body.appendChild(iframe);

  let canvas: HTMLCanvasElement;
  let cssWidth = EXPORT_WIDTH_PX;
  let cssHeight = initialHeight;
  let sectionBounds: SectionBounds[] = [];

  try {
    const iframeDoc = iframe.contentDocument;
    const iframeWin = iframe.contentWindow;
    if (!iframeDoc || !iframeWin) {
      throw new Error('Could not initialize the capture iframe.');
    }

    iframeDoc.open();
    iframeDoc.write('<!DOCTYPE html><html><head></head><body></body></html>');
    iframeDoc.close();

    const clone = prepareCaptureClone(target, iframeDoc, backgroundColor);

    await waitForIframeFonts(document, iframeDoc);
    await waitForLayout(iframeWin);

    cssHeight = measureCaptureHeight(clone, iframeDoc);
    iframe.height = String(cssHeight);
    iframe.style.height = `${cssHeight}px`;

    await waitForLayout(iframeWin);

    sectionBounds = measureSectionBounds(clone);
    const pixelRatio = computePixelRatio(EXPORT_WIDTH_PX, cssHeight);

    try {
      canvas = await toCanvas(clone, {
        width: EXPORT_WIDTH_PX,
        height: cssHeight,
        pixelRatio,
        backgroundColor,
        cacheBust: true,
        skipFonts: false,
      });
    } catch (err) {
      console.error('[exportSepPdf] html-to-image capture failed:', err);
      throw new Error(`Rendering the report to an image failed (${describeError(err)})`);
    }
  } finally {
    iframe.remove();
  }

  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error('The captured report image was empty.');
  }

  const pixelRatio = canvas.width / cssWidth;
  cssWidth = canvas.width / pixelRatio;
  cssHeight = canvas.height / pixelRatio;

  const filename = buildFilename(clientIdentifier, 'Strategic_Execution_Plan', 'pdf');
  writeSectionGroupsToPdf(jsPDF, canvas, cssWidth, sectionBounds, filename);
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message || err.name || 'Error';
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const anyErr = err as { message?: unknown; name?: unknown };
    if (typeof anyErr.message === 'string' && anyErr.message) return anyErr.message;
    if (typeof anyErr.name === 'string' && anyErr.name) return anyErr.name;
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}
